/**
 * Réordonnancement des règles : calcul pur des priorités à écrire.
 */

/** Ce que le calcul lit d'une règle : son identifiant et sa priorité (croissante = d'abord). */
export interface RegleOrdonnee {
  readonly id: number;
  readonly priorite: number;
}

/**
 * Priorités à écrire pour échanger la règle d'indice `i` avec sa voisine
 * (`delta` : -1 vers le haut, +1 vers le bas). Chaque règle déplacée reçoit de
 * préférence la priorité de l'autre ; si cela ne place pas l'ordre voulu (des
 * priorités égales départagées par l'identifiant), la plus petite priorité qui
 * convient est prise, et les règles suivantes sont décalées seulement si leur
 * propre priorité ne tient plus. Ne renvoie que les règles à modifier.
 */
export function prioritesApresDeplacement(
  triees: readonly RegleOrdonnee[],
  i: number,
  delta: -1 | 1,
): { id: number; priorite: number }[] {
  const j = i + delta;
  const a = Math.min(i, j);
  const x = triees[a + 1];
  const y = triees[a];
  if (!x || !y) return [];
  // Ordre voulu à partir de la position `a` : l'ancien second d'abord.
  const apres = [x, y, ...triees.slice(a + 2)];
  const voulues = new Map<number, number>([
    [x.id, y.priorite],
    [y.id, x.priorite],
  ]);
  const ecritures: { id: number; priorite: number }[] = [];
  let precedent: { priorite: number; id: number } | undefined = triees[a - 1];
  apres.forEach((r, k) => {
    const prefere = voulues.get(r.id) ?? r.priorite;
    // Plus petite priorité qui place `r` strictement après `precedent` ; égalité départagée par l'identifiant.
    const minimale = precedent ? (r.id > precedent.id ? precedent.priorite : precedent.priorite + 1) : prefere;
    const priorite = prefere >= minimale ? prefere : minimale;
    if (priorite !== r.priorite) ecritures.push({ id: r.id, priorite });
    // Passé les deux règles échangées, une règle inchangée arrête la propagation.
    if (k >= 2 && priorite === r.priorite) precedent = undefined;
    else precedent = { priorite, id: r.id };
  });
  return ecritures;
}
